import React, { useEffect, useState } from "react";
import { Mail, X, Sparkles, Users, CalendarDays, Bot } from "lucide-react";
import ChatPanel from "@/components/ChatPanel";
import AdminDirectChat from "@/components/AdminDirectChat";
import { BookCallModal } from "@/components/BookCall";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";

const CONTACTS = {
  email: "stu@carypactreferrals.com",
};

export default function HelpFab() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const locked = !!user?.onboarding_locked;
  const canDirectChat = !!user && !isAdmin && !user?.is_demo; // members + leaders chat with the admin
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("ai"); // "ai" | "human"
  const [bookOpen, setBookOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  // Gated new members default straight to the admin chat (AI is locked for them).
  useEffect(() => { if (locked) setTab("human"); }, [locked]);

  // Red-dot poll: unanswered admin replies for this member.
  useEffect(() => {
    if (!canDirectChat) return;
    let live = true;
    const load = async () => {
      try {
        const { data } = await api.get("/support/unread-count");
        if (live) setUnread(data.count || 0);
      } catch { /* polling — silent */ }
    };
    load();
    const t = setInterval(load, 25000);
    return () => { live = false; clearInterval(t); };
  }, [canDirectChat]);

  // Opening the chat marks messages read server-side — clear the dot.
  useEffect(() => {
    if (open && tab === "human" && canDirectChat) setUnread(0);
  }, [open, tab, canDirectChat]);

  // The read-only demo has no assistant and no chat — nothing to open.
  if (user?.is_demo) return null;

  return (
    <>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="help-fab-toggle"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-2xl text-white flex items-center justify-center transition-transform hover:scale-105"
        style={{ background: "linear-gradient(135deg, #4060FF, #6B4FFF)", boxShadow: "0 12px 32px rgba(64,96,255,0.5)" }}
        aria-label="Open help & chat"
      >
        {open ? <X size={20} /> : <Bot size={22} />}
        {!open && unread > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold text-white flex items-center justify-center"
            style={{ background: "#DC2626", boxShadow: "0 0 0 2px white" }}
            data-testid="member-chat-unread-dot"
          >
            {unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="fixed bottom-24 right-6 z-50 w-[380px] max-w-[calc(100vw-3rem)] rounded-2xl overflow-hidden fade-up"
          style={{
            background: "rgba(248,250,255,0.96)",
            backdropFilter: "blur(22px)",
            border: "1px solid rgba(15,18,38,0.10)",
            boxShadow: "0 28px 60px rgba(15,18,38,0.18)",
            height: "560px",
          }}
          data-testid="help-fab-panel"
        >
          {/* Tabs */}
          <div className="flex p-1.5 gap-1" style={{ background: "rgba(255,255,255,0.55)" }}>
            {!locked && (
              <button
                onClick={() => setTab("ai")}
                className="flex-1 py-2 rounded-lg text-xs font-medium flex items-center justify-center gap-1.5 transition-all"
                style={tab === "ai" ? { background: "white", color: "#0F1226", boxShadow: "0 2px 6px rgba(15,18,38,0.06)" } : { color: "#64748B" }}
                data-testid="help-tab-ai"
              >
                <Bot size={12} /> Ask Søren
              </button>
            )}
            {canDirectChat && (
              <button
                onClick={() => setTab("human")}
                className="flex-1 py-2 rounded-lg text-xs font-medium flex items-center justify-center gap-1.5 transition-all relative"
                style={tab === "human" ? { background: "white", color: "#0F1226", boxShadow: "0 2px 6px rgba(15,18,38,0.06)" } : { color: "#64748B" }}
                data-testid="help-tab-human"
              >
                <Users size={12} /> Message admin
                {unread > 0 && tab !== "human" && (
                  <span className="min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold text-white flex items-center justify-center" style={{ background: "#DC2626" }}>
                    {unread}
                  </span>
                )}
              </button>
            )}
          </div>

          {/* Body */}
          <div style={{ height: "calc(100% - 48px)" }}>
            {tab === "ai" && !locked ? (
              <ChatPanel compact />
            ) : canDirectChat ? (
              <AdminDirectChat contactEmail={CONTACTS.email} />
            ) : (
              <div className="p-5" data-testid="help-human-panel">
                <div className="text-xs font-mono text-ink-3 mb-1">Need a real human?</div>
                <h3 className="font-display text-lg font-semibold mb-1 tracking-tight">Stu&rsquo;s got you.</h3>
                <p className="text-sm text-ink-2 mb-4">If the AI couldn&rsquo;t unblock you, reach out directly. Most replies within an hour during US business hours.</p>
                <button onClick={() => setBookOpen(true)} className="w-full flex items-center gap-3 p-3 rounded-xl mb-2 transition-colors text-left" style={{ background: "linear-gradient(135deg, rgba(64,96,255,0.12), rgba(107,79,255,0.12))", border: "1px solid rgba(64,96,255,0.18)" }} data-testid="help-book-call">
                  <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "rgba(64,96,255,0.18)" }}><CalendarDays size={15} style={{ color: "#4060FF" }} /></div>
                  <div><div className="text-xs text-ink-3">Book a call</div><div className="text-xs font-semibold">30 min with Stuart</div></div>
                </button>
                <div className="space-y-2">
                  <a href={`mailto:${CONTACTS.email}`} className="flex items-center gap-3 p-3 rounded-xl bg-white/60 hover:bg-white transition-colors" data-testid="help-email">
                    <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "rgba(255,140,180,0.2)" }}><Mail size={15} style={{ color: "#C2185B" }} /></div>
                    <div><div className="text-xs text-ink-3">Email</div><div className="font-mono text-xs font-medium">{CONTACTS.email}</div></div>
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      <BookCallModal open={bookOpen} onClose={() => setBookOpen(false)} />
    </>
  );
}
